import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Trash2, Mail, Phone, Calendar, ChevronUp, ChevronDown,
  Clock, CheckCircle, AlertCircle, FileText, RefreshCw,
} from 'lucide-react';

export const InquiriesTab = ({ ctx }) => {
  const {
    inquiries, selectedInquiries, filterByStatus, toggleSelectAll, handleMassDelete,
    handleDeleteAll, toggleExpanded, toggleSelection, getStatusBadge, formatDate,
    updateStatus, expandedItems,
  } = ctx;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedInquiries.length === filterByStatus(inquiries).length && filterByStatus(inquiries).length > 0}
              onChange={() => toggleSelectAll('inquiries', filterByStatus(inquiries))}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-600">Select All</span>
          </label>
          {selectedInquiries.length > 0 && (
            <span className="text-sm text-blue-600 font-medium">{selectedInquiries.length} selected</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleMassDelete('inquiries')} disabled={selectedInquiries.length === 0} className="text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="h-4 w-4 mr-1" />Delete Selected
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleDeleteAll('inquiries')} className="text-red-600 border-red-200 hover:bg-red-50">
            Delete All
          </Button>
        </div>
      </div>

      {filterByStatus(inquiries).map((inquiry) => (
        <Card key={inquiry.id} className={`shadow-sm ${selectedInquiries.includes(inquiry.id) ? 'ring-2 ring-blue-500' : ''}`}>
          <CardContent className="p-0">
            <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => toggleExpanded(inquiry.id)}>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedInquiries.includes(inquiry.id)}
                    onChange={(e) => { e.stopPropagation(); toggleSelection('inquiries', inquiry.id); }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-800">{inquiry.name}</h3>
                      {getStatusBadge(inquiry.status)}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{inquiry.subject}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{inquiry.email}</span>
                      {inquiry.phone && (
                        <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{inquiry.phone}</span>
                      )}
                      <span className="flex items-center"><Calendar className="h-3 w-3 mr-1" />{formatDate(inquiry.created_at)}</span>
                    </div>
                  </div>
                </div>
                {expandedItems[inquiry.id] ? (<ChevronUp className="h-5 w-5 text-gray-400" />) : (<ChevronDown className="h-5 w-5 text-gray-400" />)}
              </div>
            </div>

            {expandedItems[inquiry.id] && (
              <div className="px-4 pb-4 border-t">
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Message:</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{inquiry.message}</p>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => updateStatus('inquiries', inquiry.id, 'contacted')} disabled={inquiry.status === 'contacted'}>
                    <Clock className="h-4 w-4 mr-1" />Mark Contacted
                  </Button>
                  <Button size="sm" onClick={() => updateStatus('inquiries', inquiry.id, 'resolved')} disabled={inquiry.status === 'resolved'} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="h-4 w-4 mr-1" />Mark Resolved
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
      {filterByStatus(inquiries).length === 0 && (
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No inquiries found</p>
        </div>
      )}
    </div>
  );
};

export const QuotesTab = ({ ctx }) => {
  const {
    quotes, selectedQuotes, filterByStatus, toggleSelectAll, handleMassDelete,
    handleDeleteAll, toggleExpanded, toggleSelection, getStatusBadge, formatDate,
    updateStatus, expandedItems,
  } = ctx;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedQuotes.length === filterByStatus(quotes).length && filterByStatus(quotes).length > 0}
              onChange={() => toggleSelectAll('quotes', filterByStatus(quotes))}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-600">Select All</span>
          </label>
          {selectedQuotes.length > 0 && (
            <span className="text-sm text-emerald-600 font-medium">{selectedQuotes.length} selected</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleMassDelete('quotes')} disabled={selectedQuotes.length === 0} className="text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="h-4 w-4 mr-1" />Delete Selected
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleDeleteAll('quotes')} className="text-red-600 border-red-200 hover:bg-red-50">
            Delete All
          </Button>
        </div>
      </div>

      {filterByStatus(quotes).map((quote) => (
        <Card key={quote.id} className={`shadow-sm ${selectedQuotes.includes(quote.id) ? 'ring-2 ring-emerald-500' : ''}`}>
          <CardContent className="p-0">
            <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => toggleExpanded(quote.id)}>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedQuotes.includes(quote.id)}
                    onChange={(e) => { e.stopPropagation(); toggleSelection('quotes', quote.id); }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-800">{quote.name}</h3>
                      {getStatusBadge(quote.status)}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        quote.insurance_type === 'life'
                          ? 'bg-blue-100 text-blue-700'
                          : quote.insurance_type === 'critical'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-cyan-100 text-cyan-700'
                      }`}>
                        {quote.insurance_type === 'life' ? 'Life Insurance' : quote.insurance_type === 'critical' ? 'Critical Illness' : 'Visitors to Canada'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Coverage: ${quote.coverage_amount?.toLocaleString()} |
                      Est. Premium: ${quote.estimated_low}-${quote.estimated_high}{quote.insurance_type === 'visitor' ? ' total' : '/mo'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{quote.email}</span>
                      <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{quote.phone}</span>
                      <span className="flex items-center"><Calendar className="h-3 w-3 mr-1" />{formatDate(quote.created_at)}</span>
                    </div>
                  </div>
                </div>
                {expandedItems[quote.id] ? (<ChevronUp className="h-5 w-5 text-gray-400" />) : (<ChevronDown className="h-5 w-5 text-gray-400" />)}
              </div>
            </div>

            {expandedItems[quote.id] && (
              <div className="px-4 pb-4 border-t">
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Age</p>
                    <p className="font-medium">{quote.age} years</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Gender</p>
                    <p className="font-medium capitalize">{quote.gender || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Smoker</p>
                    <p className="font-medium">{quote.smoker === 'yes' ? 'Yes' : quote.smoker === 'no' ? 'No' : 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Term/Duration</p>
                    <p className="font-medium">{quote.term_length === '75' ? 'To Age 75' : quote.insurance_type === 'visitor' ? `${quote.term_length} Days` : `${quote.term_length} Years`}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => updateStatus('quotes', quote.id, 'contacted')} disabled={quote.status === 'contacted'}>
                    <Clock className="h-4 w-4 mr-1" />Mark Contacted
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus('quotes', quote.id, 'quoted')} disabled={quote.status === 'quoted'}>
                    <FileText className="h-4 w-4 mr-1" />Mark Quoted
                  </Button>
                  <Button size="sm" onClick={() => updateStatus('quotes', quote.id, 'converted')} disabled={quote.status === 'converted'} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="h-4 w-4 mr-1" />Mark Converted
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
      {filterByStatus(quotes).length === 0 && (
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No quote requests found</p>
        </div>
      )}
    </div>
  );
};

export const ReferralsTab = ({ ctx }) => {
  const {
    referrals, selectedReferrals, filterByStatus, toggleSelectAll, handleMassDelete,
    handleDeleteAll, toggleExpanded, toggleSelection, getStatusBadge, formatDate,
    updateStatus, expandedItems,
  } = ctx;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedReferrals.length === filterByStatus(referrals).length && filterByStatus(referrals).length > 0}
              onChange={() => toggleSelectAll('referrals', filterByStatus(referrals))}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-600">Select All</span>
          </label>
          {selectedReferrals.length > 0 && (
            <span className="text-sm text-violet-600 font-medium">{selectedReferrals.length} selected</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleMassDelete('referrals')} disabled={selectedReferrals.length === 0} className="text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="h-4 w-4 mr-1" />Delete Selected
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleDeleteAll('referrals')} className="text-red-600 border-red-200 hover:bg-red-50">
            Delete All
          </Button>
        </div>
      </div>

      {filterByStatus(referrals).map((referral) => (
        <Card key={referral.id} className={`shadow-sm ${selectedReferrals.includes(referral.id) ? 'ring-2 ring-violet-500' : ''}`}>
          <CardContent className="p-0">
            <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => toggleExpanded(referral.id)}>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedReferrals.includes(referral.id)}
                    onChange={(e) => { e.stopPropagation(); toggleSelection('referrals', referral.id); }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-800">{referral.client_first_name} {referral.client_last_name}</h3>
                      {getStatusBadge(referral.status)}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Referred by: {referral.referrer_name} |
                      Province: {referral.client_province}
                      {referral.total_debt && ` | Debt: ${referral.total_debt}`}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center"><Mail className="h-3 w-3 mr-1" />{referral.client_email}</span>
                      <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{referral.client_phone}</span>
                      <span className="flex items-center"><Calendar className="h-3 w-3 mr-1" />{formatDate(referral.created_at)}</span>
                    </div>
                  </div>
                </div>
                {expandedItems[referral.id] ? (<ChevronUp className="h-5 w-5 text-gray-400" />) : (<ChevronDown className="h-5 w-5 text-gray-400" />)}
              </div>
            </div>

            {expandedItems[referral.id] && (
              <div className="px-4 pb-4 border-t">
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Referrer Info</h4>
                    <p className="text-sm"><strong>Name:</strong> {referral.referrer_name}</p>
                    <p className="text-sm"><strong>Email:</strong> {referral.referrer_email}</p>
                    <p className="text-sm"><strong>Phone:</strong> {referral.referrer_phone}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-purple-800 mb-2">Client Info</h4>
                    <p className="text-sm"><strong>Name:</strong> {referral.client_first_name} {referral.client_last_name}</p>
                    <p className="text-sm"><strong>Email:</strong> {referral.client_email}</p>
                    <p className="text-sm"><strong>Phone:</strong> {referral.client_phone}</p>
                    <p className="text-sm"><strong>Province:</strong> {referral.client_province}</p>
                    {referral.total_debt && (
                      <p className="text-sm"><strong>Est. Debt:</strong> {referral.total_debt}</p>
                    )}
                  </div>
                </div>
                {referral.notes && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Notes:</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{referral.notes}</p>
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => updateStatus('referrals', referral.id, 'contacted')} disabled={referral.status === 'contacted'}>
                    <Clock className="h-4 w-4 mr-1" />Mark Contacted
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus('referrals', referral.id, 'in_progress')} disabled={referral.status === 'in_progress'}>
                    <RefreshCw className="h-4 w-4 mr-1" />In Progress
                  </Button>
                  <Button size="sm" onClick={() => updateStatus('referrals', referral.id, 'completed')} disabled={referral.status === 'completed'} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="h-4 w-4 mr-1" />Mark Completed
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
      {filterByStatus(referrals).length === 0 && (
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No referrals found</p>
        </div>
      )}
    </div>
  );
};
